import { AlertTriangle, CircleSlash, Inbox, Loader, LogIn, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'

import { DiagnosticCopy } from './DiagnosticCopy'

/**
 * Состояние экрана: загрузка, пусто, нет доступа, недоступно, ошибка. Каждое
 * отличается значком, рамкой и текстом, а не только цветом — экран в
 * `filter: grayscale(1)` остаётся читаемым (CLAUDE.md §7).
 */
export type StatusKind = 'loading' | 'empty' | 'forbidden' | 'unauthorized' | 'unavailable' | 'error'

const ICONS: Record<StatusKind, ReactNode> = {
  loading: <Loader size={18} aria-hidden="true" />,
  empty: <Inbox size={18} aria-hidden="true" />,
  forbidden: <CircleSlash size={18} aria-hidden="true" />,
  unauthorized: <LogIn size={18} aria-hidden="true" />,
  unavailable: <WifiOff size={18} aria-hidden="true" />,
  error: <AlertTriangle size={18} aria-hidden="true" />,
}

const TONES: Record<StatusKind, 'neutral' | 'warning' | 'danger'> = {
  loading: 'neutral',
  empty: 'neutral',
  forbidden: 'warning',
  unauthorized: 'warning',
  unavailable: 'warning',
  error: 'danger',
}

export function StatusMessage({
  kind,
  title,
  text,
  meta,
  action,
}: {
  kind: StatusKind
  title: string
  text?: string
  /** Служебная строка: код и корреляция для отчёта. */
  meta?: string
  action?: ReactNode
}) {
  const urgent = kind === 'error' || kind === 'forbidden'
  return (
    <div className="krp-status" data-tone={TONES[kind]} role={urgent ? 'alert' : 'status'} data-status={kind}>
      {ICONS[kind]}
      <div className="krp-status__body">
        <p className="krp-status__title">{title}</p>
        {text !== undefined && <p className="krp-status__text">{text}</p>}
        {meta !== undefined && <p className="krp-status__meta">{meta}</p>}
        {kind !== 'loading' && kind !== 'empty' && <DiagnosticCopy text={[title, text, meta].filter(Boolean).join('\n')} />}
        {/* Пустое действие (`undefined`, `null`, `false`) не оставляет пустой строки с отступом. */}
        {action !== undefined && action !== null && action !== false && (
          <div className="krp-status__action">{action}</div>
        )}
      </div>
    </div>
  )
}
